import detailMessage from './detailMessage';
import React, { useState, ChangeEvent, KeyboardEvent } from "react";
import { dismissMfaSetupPrompt, enableEmailMfa, setupTotpMfa, enableTotpMfa } from "./api";
import Logo from "./components/Logo";
import { useLang } from "./LangContext";
import "./App.css";
import "./MfaSetupPage.css";

interface TotpData {
  secret: string;
  uri: string;
}

type Step = "choose" | "totp";

interface Props {
  token: string;
  onComplete: () => void;
  onSkip: () => void;
}

export default function MfaSetupPage({ token, onComplete, onSkip }: Props) {
  const { t } = useLang();
  const [step, setStep] = useState<Step>("choose");
  const [totpData, setTotpData] = useState<TotpData | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSkip = async () => {
    setLoading(true);
    setError("");
    try {
      await dismissMfaSetupPrompt(token);
      onSkip();
    } catch (err) {
      setError(detailMessage(err, t.mfaCouldNotSkip));
      setLoading(false);
    }
  };

  const handleEnableEmail = async () => {
    setLoading(true);
    setError("");
    try {
      await enableEmailMfa(token);
      onComplete();
    } catch (err) {
      setError(detailMessage(err, t.mfaFailedEnableEmail));
      setLoading(false);
    }
  };

  const handleStartTotp = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await setupTotpMfa(token);
      setTotpData(data);
      setTotpCode("");
      setStep("totp");
    } catch (err) {
      setError(detailMessage(err, t.mfaFailedStartSetup));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmTotp = async () => {
    setLoading(true);
    setError("");
    try {
      await enableTotpMfa(token, totpCode);
      onComplete();
    } catch (err) {
      setError(detailMessage(err, t.mfaInvalidCode));
      setLoading(false);
    }
  };

  return (
    <main className="login-container">
      <div className="login-card mfa-setup-card">
        <div className="login-brand">
          <Logo size={40} />
        </div>
        <h2>{t.secureYourAccount}</h2>
        <p className="mfa-setup-intro">
          {t.mfaSetupIntro} <strong>{t.mfaSetupIntroStrong}</strong>
          {' '}{t.mfaSetupIntroReason}
        </p>

        {error && <div className="error-message">{error}</div>}

        {step === "choose" && (
          <>
            <div className="mfa-setup-options">
              <button
                className="mfa-setup-option"
                onClick={handleEnableEmail}
                disabled={loading}
              >
                <span className="mfa-option-icon">✉️</span>
                <span className="mfa-option-label">{t.mfaEmailLabel}</span>
                <span className="mfa-option-desc">{t.mfaEmailDesc}</span>
              </button>
              <button
                className="mfa-setup-option"
                onClick={handleStartTotp}
                disabled={loading}
              >
                <span className="mfa-option-icon">🔑</span>
                <span className="mfa-option-label">{t.mfaAppLabel}</span>
                <span className="mfa-option-desc">{t.mfaAppDesc}</span>
              </button>
            </div>
            <div className="mfa-setup-skip">
              <button
                type="button"
                className="link-button"
                onClick={handleSkip}
                disabled={loading}
              >
                {t.mfaSkipForNow}
              </button>
              <p className="mfa-setup-skip-hint">
                {t.mfaSkipHint}
              </p>
            </div>
          </>
        )}

        {step === "totp" && totpData && (
          <div className="mfa-totp-confirm">
            <p>{t.mfaTotpInstructions}</p>
            <div className="totp-secret">{totpData.secret}</div>
            <p className="mfa-hint">{t.mfaTotpHint}</p>
            <input
              className="input"
              placeholder={t.mfaCodePlaceholder}
              value={totpCode}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
                e.key === "Enter" && totpCode.length === 6 && handleConfirmTotp()
              }
              maxLength={6}
              autoFocus
            />
            <div className="mfa-setup-actions">
              <button
                className="button login-button"
                onClick={handleConfirmTotp}
                disabled={loading || totpCode.length < 6}
              >
                {loading ? t.mfaVerifying : t.mfaConfirmEnable}
              </button>
              <button
                className="link-button"
                type="button"
                onClick={() => { setStep("choose"); setTotpData(null); setError(""); }}
              >
                {t.mfaBack}
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
